import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { normalizeSkill } from "@/utils/skillNormalization";
import { matchesJobSearch } from "@/utils/searchUtils";
import { generateDummyJobs } from "@/utils/dummy-data/jobs";
import { SortField, SortOrder } from "@/components/candidates/CandidatesViewOptions";
import { isClosedMatchStatusValue } from "@/utils/matches/status";


export interface JobData {
    id: string;
    title: string;
    company: {
        id: string;
        name: string;
    };
    skills: string[];
    createdAt: string;
    updated_at?: string;
    description?: string;
    aiSummary?: string;
    matchCount?: number;
    contactId?: string;
    published?: boolean;
    publishdate?: string | null;
    // Add status field
    status?: string;

    // Fields
    employmentType?: string;
    timeCommitment?: string;
    contractDuration?: string;
    startDate?: string;
    endDate?: string;
    seniorityLevel?: string;
    budget?: string;
    budgetFreelance?: string;
    locationType?: string;
    workplaceBreakdown?: string;
    hardSkills?: string[];
    softSkills?: string[];
    educationLevel?: string;
    yearsOfExperience?: string;
    languageRequirements?: string;
    extraLang?: string;
    cultureTags?: string[];
    applicationDeadline?: string;
    jobPostingDate?: string;
    source?: string;
    assignedRecruiter?: string;
    employmentClassification?: string;
    // New fields for grid view
    applicationsCount?: number;
    processesCount?: number;
    newApplicationsCount?: number;
    // AI analysis for skill extraction
    aiAnalysis?: {
        skills_required?: {
            "Extracted Value"?: string[];
        };
        technologies_handled?: {
            "Extracted Value"?: string[];
        };
    };
}

export const useJobsData = () => {
    const [jobs, setJobs] = useState<JobData[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [sortField, setSortField] = useState<SortField>("dateCreated");
    const [sortOrder, setSortOrder] = useState<SortOrder>("desc");

    const fetchJobs = useCallback(async () => {
        try {
            setIsLoading(true);

            let dbSortField = "created_at"; // default
            if (sortField === "name") {
                dbSortField = "title";
            } else if (sortField === "matchScore") {
                dbSortField = "match_count";
            } else if (sortField === "dateCreated") {
                dbSortField = "created_at";
            }

            const query = supabase
                .from('jobs_with_notifications')
                .select(`
            id,
            title,
            description,
            skills,
            created_at,
            contact_id,
            company_id,
            companies(id, name),
            employment_type,
            time_commitment,
            contract_duration,
            start_date,
            end_date,
            seniority_level,
            budget,
            budget_freelance,
            location_type,
            workplace_breakdown,
            hard_skills,
            soft_skills,
            education_level,
            years_of_experience,
            language_requirements,
            extra_lang,
            culture_tags,
            application_deadline,
            job_posting_date,
            source,
            assigned_recruiter,
            employment_classification,
            ai_summary,
            ai_analysis,
            published,
            publishdate,
            status,
            updated_at,
            new_applications_count
          `)
                .order(dbSortField, { ascending: sortOrder === "asc" });

            const { data, error } = await query;

            if (error) {
                throw error;
            }

            if (!data) {
                setJobs([]);
                return;
            }

            // Fetch all matches for these jobs
            const { data: matchesAgg, error: matchesAggError } = await supabase
                .from('matches')
                .select('job_id, status')
                .in('job_id', data.map((job: any) => job.id));

            // Build a map: jobId -> { applicationsCount, processesCount }
            const matchCountsMap: Record<string, { applicationsCount: number; processesCount: number }> = {};
            if (!matchesAggError && matchesAgg) {
                for (const row of matchesAgg) {
                    const jobId = row.job_id;
                    const status = row.status;
                    if (!matchCountsMap[jobId]) {
                        matchCountsMap[jobId] = { applicationsCount: 0, processesCount: 0 };
                    }
                    if (status === 'applied') {
                        matchCountsMap[jobId].applicationsCount += 1;
                    } else if (!isClosedMatchStatusValue(status)) {
                        matchCountsMap[jobId].processesCount += 1;
                    }
                }
            }

            const formattedJobs: JobData[] = data.map(job => ({
                id: job.id,
                title: job.title,
                company: {
                    id: job.company_id,
                    name: job.companies?.name || 'Unknown Company'
                },
                skills: job.skills || [],
                createdAt: job.created_at,
                updated_at: job.updated_at,
                description: job.description,
                aiSummary: job.ai_summary,
                aiAnalysis: job.ai_analysis,
                contactId: job.contact_id,
                published: job.published,
                publishdate: job.publishdate,
                // Add status mapping
                status: job.status,
                employmentType: job.employment_type,
                timeCommitment: job.time_commitment,
                contractDuration: job.contract_duration,
                startDate: job.start_date,
                endDate: job.end_date,
                seniorityLevel: job.seniority_level,
                budget: job.budget,
                budgetFreelance: job.budget_freelance,
                locationType: job.location_type,
                workplaceBreakdown: job.workplace_breakdown,
                hardSkills: job.hard_skills,
                softSkills: job.soft_skills,
                educationLevel: job.education_level,
                yearsOfExperience: job.years_of_experience,
                languageRequirements: job.language_requirements,
                extraLang: job.extra_lang,
                cultureTags: job.culture_tags,
                applicationDeadline: job.application_deadline,
                jobPostingDate: job.job_posting_date,
                source: job.source,
                assignedRecruiter: job.assigned_recruiter,
                employmentClassification: job.employment_classification,
                // New fields for grid view
                applicationsCount: matchCountsMap[job.id]?.applicationsCount || 0,
                processesCount: matchCountsMap[job.id]?.processesCount || 0,
                newApplicationsCount: job.new_applications_count || 0,
            }));

            setJobs(formattedJobs);
        } catch (error) {
            console.error("Error fetching jobs:", error);
            toast({
                title: "Error",
                description: "Failed to load job offers",
                variant: "destructive",
            });
        } finally {
            setIsLoading(false);
        }
    }, [sortField, sortOrder]);

    useEffect(() => {
        fetchJobs();
    }, [fetchJobs]);

    const filteredJobs = jobs.filter(job => {
        return matchesJobSearch(job, searchTerm);
    });

    const addJob = async (jobData: any): Promise<string | undefined> => {
        //console.log('Adding job with data:', jobData);

        // Validate required fields
        if (!jobData.title || !jobData.companyId) {
            toast({
                title: "Error",
                description: "Title and company are required fields",
                variant: "destructive",
            });
            return undefined;
        }

        try {
            const { data: sessionData } = await supabase.auth.getSession();
            const userUuid = sessionData.session?.user?.id;

            let organizationUuid = null;

            if (userUuid) {
                const { data: userOrg } = await supabase
                    .from('user_organization')
                    .select('organization_uuid')
                    .eq('user_uuid', userUuid)
                    .single();

                organizationUuid = userOrg?.organization_uuid;
            }

            const skillsArray = jobData.skills
                ? Array.isArray(jobData.skills) ? jobData.skills
                    : typeof jobData.skills === 'string' ? jobData.skills.split(',').map((skill: string) => skill.trim())
                        : []
                : [];

            //console.log('Inserting job with these skills:', skillsArray);
            //console.log('AI Summary value:', jobData.ai_summary);

            // Only include fields that have values
            const insertData = {
                title: jobData.title,
                company_id: jobData.companyId,
                description: jobData.description || null,
                skills: skillsArray.length > 0 ? skillsArray : null,
                contact_id: jobData.contactId || null,
                organization_uuid: organizationUuid,
                // Add status field
                ...(jobData.status && { status: jobData.status }),
                // Additional fields with new field mappings - only include if they have values
                ...(jobData.employment_type && { employment_type: jobData.employment_type }),
                ...(jobData.time_commitment && { time_commitment: jobData.time_commitment }),
                ...(jobData.contract_duration && { contract_duration: jobData.contract_duration }),
                ...(jobData.start_date && { start_date: jobData.start_date }),
                ...(jobData.end_date && { end_date: jobData.end_date }),
                ...(jobData.seniority_level && { seniority_level: jobData.seniority_level }),
                ...(jobData.budget && { budget: jobData.budget }),
                ...(jobData.budget_freelance && { budget_freelance: jobData.budget_freelance }),
                ...(jobData.location_type && { location_type: jobData.location_type }),
                ...(jobData.workplace_breakdown && { workplace_breakdown: jobData.workplace_breakdown }),
                ...(jobData.hard_skills && { hard_skills: jobData.hard_skills }),
                ...(jobData.soft_skills && { soft_skills: jobData.soft_skills }),
                ...(jobData.education_level && { education_level: jobData.education_level }),
                ...(jobData.years_of_experience && { years_of_experience: jobData.years_of_experience }),
                ...(jobData.language_requirements && { language_requirements: jobData.language_requirements }),
                ...(jobData.extra_lang && { extra_lang: jobData.extra_lang }),
                ...(jobData.culture_tags && { culture_tags: jobData.culture_tags }),
                ...(jobData.application_deadline && { application_deadline: jobData.application_deadline }),
                ...(jobData.job_posting_date && { job_posting_date: jobData.job_posting_date }),
                ...(jobData.source && { source: jobData.source }),
                ...(jobData.assigned_recruiter && { assigned_recruiter: jobData.assigned_recruiter }),
                ...(jobData.employment_classification && { employment_classification: jobData.employment_classification }),
                ...(jobData.ai_summary && { ai_summary: jobData.ai_summary })
            };

            //console.log('Final insert data:', insertData);

            const { data, error } = await supabase
                .from('jobs')
                .insert(insertData)
                .select()
                .single();

            if (error) {
                console.error("Supabase error when adding job:", error);
                throw error;
            }

            await triggerAIAnalysisForJob(data.id);
            document.location.href = `/jobs/${data.id}`; // Redirect to the job detail page

            // Call fetchJobs to refresh the jobs list
            fetchJobs();

            toast({
                title: "Success",
                description: "Job offer has been created successfully",
            });

            return data.id;
        } catch (error) {
            console.error("Error adding job:", error);
            toast({
                title: "Error",
                description: "Failed to create job offer: " + (error instanceof Error ? error.message : "Unknown error"),
                variant: "destructive",
            });
            return undefined;
        }
    };

    const generateJobs = async () => {
        try {
            await generateDummyJobs();
            fetchJobs();
            toast({
                title: "Success",
                description: "Dummy jobs have been generated successfully",
            });
            return true;
        } catch (error) {
            console.error("Error generating jobs:", error);
            toast({
                title: "Error",
                description: "Failed to generate dummy jobs",
                variant: "destructive",
            });
            return false;
        }
    };

    const deleteJobs = async (ids: string[]): Promise<void> => {
        if (!ids || ids.length === 0) return;
        try {
            // Delete associated matches and comments for each job
            for (const jobId of ids) {
                await supabase.from('matches').delete().eq('job_id', jobId);
                await supabase.from('job_comments').delete().eq('job_id', jobId);
            }
            // Delete the jobs
            const { error } = await supabase.from('jobs').delete().in('id', ids);
            if (error) throw error;
            toast({
                title: 'Success',
                description: `Successfully deleted ${ids.length} job(s).`,
            });
            fetchJobs();
        } catch (error) {
            console.error('Error deleting jobs:', error);
            toast({
                title: 'Error',
                description: 'Failed to delete selected jobs.',
                variant: 'destructive',
            });
        }
    };

    return {
        jobs: filteredJobs,
        isLoading,
        searchTerm,
        setSearchTerm,
        sortField,
        setSortField,
        sortOrder,
        setSortOrder,
        addJob,
        generateJobs,
        fetchJobs,
        deleteJobs
    };
};

export const triggerAIAnalysisForJob = async (jobId: string) => {

    //console.log('Triggering AI analysis for job ID:', jobId);

    const { data: analysisData, error: analysisError } = await supabase.functions.invoke(
        'ai-processing?command=push-new-analysis',
        {
            body: {
                entityId: jobId,
                entityType: "job",
                analysisType: "analyse-job-offer",
                actionOnComplete: "update-job",
            }
        }
    );

    if (analysisError) {
        console.error("Error analyzing job offer:", analysisError);
    }

    return;
}
